import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import AuthShell from "@/components/AuthShell";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const inputCls =
  "w-full rounded-xl border border-white/12 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-arjuna-teal focus:outline-none focus:ring-1 focus:ring-arjuna-teal/40 transition-colors duration-300";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/access-key", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/access-key");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      subtitle="Access the Arjuna Space intelligence console."
      footer={
        <>
          New to Arjuna Space?{" "}
          <Link to="/register" data-testid="link-register" className="text-arjuna-teal hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <label className="overline mb-2 block">Email</label>
          <input data-testid="login-email" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@org.in" />
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="overline">Password</label>
            <Link to="/forgot-password" data-testid="link-forgot" className="text-xs text-slate-400 hover:text-arjuna-teal">
              Forgot?
            </Link>
          </div>
          <input data-testid="login-password" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit" className="btn-primary w-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthShell>
  );
}
